import React, {FC, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps, useAppDispatch} from '../../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../../src/helpers/reselectHelpers';
import {CrmListModel} from '../../../../../../../../../src/models/api/crmListModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {
  CrmSyncSearchOptions,
  fetchListAsyncAction,
  searchListsAsyncAction,
} from '../../../../../../../../../src/store/data/crmSync/crmSyncActions';
import {makeGetCrmSyncViaSourceAndResourceType} from '../../../../../../../../../src/store/data/crmSync/crmSyncSelectors';
import {getIsUserGlobalAdmin} from '../../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncOptionsEnum, CrmSyncsOptions, ListErrorsEnum, SelectedOption} from './crmSyncsOptions';

/*
 * Props.
 */

interface CrmSyncsOptionsContainerPublicProps {
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  isLoading: boolean;
  isDisabled: boolean;
  allResourceCount?: number;
  selectedOption?: SelectedOption;
  disabledOptionType?: CrmSyncOptionsEnum;
  onSelectOption: HandlerOf<SelectedOption>;
}
type CrmSyncsOptionsContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type CrmSyncsOptionsContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type CrmSyncsOptionsContainerComponentProps = CrmSyncsOptionsContainerPublicProps &
  CrmSyncsOptionsContainerStateProps &
  CrmSyncsOptionsContainerDispatchProps;

/*
 * Component.
 */

const CrmSyncsOptionsContainerComponent: FC<CrmSyncsOptionsContainerComponentProps> = (props) => {
  const {
    isAdmin,
    isLoading,
    isDisabled,
    source,
    resourceType,
    crmSync,
    selectedOption,
    allResourceCount,
    disabledOptionType,
    onSelectOption,
    searchListsAsync,
  } = props;

  const config = useCrmPageConfig(source);
  const resourceConfig = config[resourceType];
  const [list, setList] = useState<CrmListModel>();
  const [listError, setListError] = useState<ListErrorsEnum>();
  const [isFetchingList, setIsFetchingList] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const onSelect = (type: CrmSyncOptionsEnum, listId?: string) => {
    onSelectOption({type, listId});
    // if we are choosing the list option and the existing list choice is over the max count
    if (type === CrmSyncOptionsEnum.LISTS && isMaxCountExcceeded(resourceConfig?.maxNumberToSync, list)) {
      setListError(ListErrorsEnum.MAX_LIMIT_REACHED);
      return;
    }
    // clear list error if we choose a different option
    setListError(undefined);
  };

  const numOptions = resourceConfig && Object.keys(resourceConfig.syncOptions).length;
  const shouldRenderOptions = numOptions && numOptions > 1;

  // when the selected list changes, fetch the list
  useEffect(() => {
    if (!shouldRenderOptions || !selectedOption?.listId || !crmSync?.id) {
      return;
    }

    // fetch the selected list so we can display the name/count
    const onFetchListAsync = async (listId: string, crmSyncId: number) => {
      try {
        setIsFetchingList(true);
        const fetchedList = await dispatch(fetchListAsyncAction(listId, crmSyncId));
        setList(fetchedList);

        if (isMaxCountExcceeded(resourceConfig?.maxNumberToSync, fetchedList)) {
          setListError(ListErrorsEnum.MAX_LIMIT_REACHED);
        } else {
          setListError(undefined);
        }
      } catch (error) {
        if (error.status === 404) {
          setListError(ListErrorsEnum.LIST_DELETED);
        } else {
          setListError(ListErrorsEnum.UNKNOWN);
        }
      }
      setIsFetchingList(false);
    };

    onFetchListAsync(selectedOption?.listId, crmSync.id);
  }, [shouldRenderOptions, crmSync?.id, selectedOption?.listId, dispatch, resourceConfig?.maxNumberToSync]);

  if (!shouldRenderOptions) {
    return null;
  }

  return (
    <CrmSyncsOptions
      isLoading={isLoading}
      isDisabled={isDisabled}
      isAdmin={isAdmin}
      list={list}
      listError={listError}
      isLoadingList={isFetchingList}
      resourceConfig={resourceConfig}
      crmDisplayName={config.displayName}
      selectedOption={selectedOption}
      disabledOptionType={disabledOptionType}
      onSelect={onSelect}
      allResourceCount={allResourceCount}
      searchListsAsync={searchListsAsync}
    />
  );
};

/*
 * Helpers.
 */

function isMaxCountExcceeded(maxCount?: number, list?: CrmListModel) {
  if (!maxCount || !list?.numResources) {
    return false;
  }

  return list.numResources > maxCount;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CrmSyncsOptionsContainerPublicProps>()(() => ({
  isAdmin: getIsUserGlobalAdmin,
  crmSync: makeGetCrmSyncViaSourceAndResourceType(),
}));

const mapDispatchToProps = makeMapDispatchToProps<CrmSyncsOptionsContainerPublicProps>()((props) => ({
  searchListsAsync: (query: string, options: CrmSyncSearchOptions) =>
    searchListsAsyncAction(props.source, props.resourceType, query, options),
}));

export const CrmSyncsOptionsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CrmSyncsOptionsContainerComponent);
